import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Row, Col, Divider, Spin, Modal, Skeleton } from 'antd';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { Method_POST_REST } from '../../../../service/api';
import * as XLSX from 'xlsx';
import { useDraggableModal } from '../Balancete/utils/modalDraggable';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from 'chart.js';

// Importação dos sub-componentes
import DashboardHeader from './components/DashboardHeader';
import OperationStats from './components/OperationStats';
import AccountingLevelMix from './components/AccountingLevelMix';
import CriticalActions from './components/CriticalActions';
import MonthlyEvolution from './components/MonthlyEvolution';
import OpenFinanceStatus from './components/OpenFinanceStatus';
import DocumentProcessing from './components/DocumentProcessing';
import AutomationRadar from './components/AutomationRadar';
import AnomalyAlerts from './components/AnomalyAlerts';
import YearlyObligations from './components/YearlyObligations';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ChartTitle,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'bottom', labels: { usePointStyle: true, padding: 20, font: { size: 12 } } },
  },
  scales: {
    y: { grid: { borderDash: [5, 5], color: '#f1f5f9' }, ticks: { font: { size: 11 } } },
    x: { grid: { display: false }, ticks: { font: { size: 11 } } }
  }
};

export default function ContabilidadeDashboard({ visible, onClose, zIndex, onFocus, title }) {
  const [periodo, setPeriodo] = useState([dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')]);
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [openFinanceRawData, setOpenFinanceRawData] = useState([]);
  const [pimPerformanceRawData, setPimPerformanceRawData] = useState([]);
  const [obligationsData, setObligationsData] = useState([]);
  const [anomaliesData, setAnomaliesData] = useState([]);

  const draggableProps = useDraggableModal(zIndex, onFocus, {
    visible,
    modalKey: "dashboardContabil",
    initialWidth: 1300
  });

  const fetchDashboardData = useCallback(async () => {
    if (!periodo || !periodo[0] || !periodo[1]) return;

    const cacheKey = `dashboard_contabil_cache_${periodo[0].format('YYYY-MM')}_${periodo[1].format('YYYY-MM')}`;
    const cachedStr = localStorage.getItem(cacheKey);

    if (cachedStr) {
      try {
        const cached = JSON.parse(cachedStr);
        // 1 hour = 3600000 ms
        if (cached.timestamp && (Date.now() - cached.timestamp < 3600000)) {
          setDashboardData(cached.dashboardData);
          setOpenFinanceRawData(cached.openFinanceRawData);
          setPimPerformanceRawData(cached.pimPerformanceRawData);
          setObligationsData(cached.obligationsData);
          setAnomaliesData(cached.anomaliesData);
          return;
        }
      } catch (e) {
        console.warn('Erro ao ler o cache do dashboard', e);
      }
    }

    setLoading(true);
    try {
      const targetYear = periodo[1].year();
      const [resNivel, resOpenFinance, resPim, resObligations, resAnomalies] = await Promise.all([
        Method_POST_REST("contabilidade/dashboard/empresas/nivel", {
          dataInicio: periodo[0].toISOString(),
          dataFim: periodo[1].toISOString()
        }),
        Method_POST_REST("contabilidade/dashboard/empresas/open-finance", {}),
        Method_POST_REST("contabilidade/dashboard/automacao/pim-performance", {}),
        Method_POST_REST("dashboard/agenda/listar", {
          obrigacao: { tipo: { id: "D" }, categoria: { id: "C" } },
          dataVencimento: periodo[1].toISOString()
        }),
        Method_POST_REST(`contabilidade/dashboard/anomalias/caixa?ano=${targetYear}`, {})
      ]);

      const rawDashboardData = (resNivel && resNivel !== "Foi") ? resNivel : null;
      let resolvedDashboardData = rawDashboardData;
      if (rawDashboardData) {
        const empresasOrig = Array.isArray(rawDashboardData) ? rawDashboardData : (rawDashboardData.clientes || []);
        const strippedEmpresas = empresasOrig.map(e => ({
          id: e.id,
          cnpj: e.cnpj,
          nomeFantasia: e.nomeFantasia,
          nome: e.nome,
          nivelContabil: e.nivelContabil,
          regimeTributario: e.regimeTributario ? { id: e.regimeTributario.id } : null,
          segmentoEmpresa: e.segmentoEmpresa ? { id: e.segmentoEmpresa.id } : null,
          carteira: e.carteira ? { id: e.carteira.id } : null,
        }));
        resolvedDashboardData = Array.isArray(rawDashboardData) ? strippedEmpresas : { ...rawDashboardData, clientes: strippedEmpresas };
      }

      if (resolvedDashboardData) {
        setDashboardData(resolvedDashboardData);
      }

      const rawOF = Array.isArray(resOpenFinance?.clientes) ? resOpenFinance.clientes : [];
      const listaClientesOF = rawOF.map(c => ({ id: c.id }));
      setOpenFinanceRawData(listaClientesOF);

      const rawPim = Array.isArray(resPim?.clientes) ? resPim.clientes : (Array.isArray(resPim) ? resPim : []);
      const listaPim = rawPim.map(c => ({ id: c.id }));
      setPimPerformanceRawData(listaPim);

      const targetIds = [1, 308, 9, 14, 15, 255, 2586, 2587, 41];
      let resolvedObligations = [];
      if (resObligations && Array.isArray(resObligations)) {
        resolvedObligations = resObligations.filter(item => targetIds.includes(item.obrigacao?.id)).map(item => ({
           obrigacao: { id: item.obrigacao?.id, sigla: item.obrigacao?.sigla, nome: item.obrigacao?.nome },
           quantidadePrevisto: item.quantidadePrevisto,
           quantidadeEmitido: item.quantidadeEmitido,
        }));
      }
      setObligationsData(resolvedObligations);

      const resolvedAnomalies = (resAnomalies?.detalhes || []).map(item => ({
        mes: item.mes,
        ano: item.ano,
        status: item.status,
        tipoAnomalia: item.tipoAnomalia,
        valorNegativo: item.valorNegativo,
        valorAnomalia: item.valorAnomalia,
        cliente: {
           id: item.cliente?.id,
           nomeFantasia: item.cliente?.nomeFantasia,
           nome: item.cliente?.nome,
           carteira: item.cliente?.carteira ? { id: item.cliente.carteira.id } : null,
        }
      }));
      setAnomaliesData(resolvedAnomalies);

      // Save to cache
      const cacheData = {
        timestamp: Date.now(),
        dashboardData: resolvedDashboardData,
        openFinanceRawData: listaClientesOF,
        pimPerformanceRawData: listaPim,
        obligationsData: resolvedObligations,
        anomaliesData: resolvedAnomalies
      };
      
      try {
        localStorage.setItem(cacheKey, JSON.stringify(cacheData));
      } catch (storageError) {
        console.warn('Não foi possível salvar no cache (limite de quota excedido)', storageError);
        // Tenta limpar caches antigos
        try {
          const keysToRemove = [];
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith('dashboard_contabil_cache_')) {
              keysToRemove.push(key);
            }
          }
          keysToRemove.forEach(k => localStorage.removeItem(k));
          
          // Tenta salvar novamente
          localStorage.setItem(cacheKey, JSON.stringify(cacheData));
        } catch (e) {
          console.warn('Ainda excedendo a quota após limpeza, cache ignorado', e);
        }
      }

    } catch (error) {
      console.error("Erro ao buscar dados do dashboard:", error);
      toast.error("Erro ao sincronizar dados do dashboard.");
    } finally {
      setLoading(false);
    }
  }, [periodo]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Processamento da lista que vem da API
  const empresas = Array.isArray(dashboardData) ? dashboardData : (dashboardData?.clientes || []);

  const countByLevel = (level) => empresas.filter(e => e.nivelContabil === level).length;
  const countSemInfo = empresas.filter(e => !e.nivelContabil).length;

  const totalEmpresas = empresas.length;
  const bancoCount = countByLevel('B');
  const planilhaCount = countByLevel('P');
  const caixaCount = countByLevel('C');
  const semInfoCount = countSemInfo;

  // Gráfico de Nível Contábil - Dados Reais
  const nivelContabilData = {
    labels: ['Banco', 'Planilha', 'Caixa', 'Sem Info'],
    datasets: [{
      data: [bancoCount, planilhaCount, caixaCount, semInfoCount],
      backgroundColor: ['#2563eb', '#8b5cf6', '#10b981', '#ef4444'],
      borderWidth: 0,
      hoverOffset: 10
    }]
  };

  const evolutionData = null; // Removido mock


  const openFinanceData = {
    labels: ['Conectadas', 'Expiradas', 'Sem Adesão'],
    datasets: [{
      data: [
        openFinanceRawData.length,
        0,
        Math.max(0, totalEmpresas - openFinanceRawData.length)
      ],
      backgroundColor: ['#10b981', '#f59e0b', '#ef4444'],
      borderWidth: 0,
      hoverOffset: 10
    }]
  };



  const automationRadarData = {
    labels: ['Feito pelo PIM', 'Intervenção Humana'],
    datasets: [{
      data: [
        pimPerformanceRawData.length,
        Math.max(0, totalEmpresas - pimPerformanceRawData.length)
      ],
      backgroundColor: ['#071d9bff', '#e2e8f0'],
      borderWidth: 0,
      hoverOffset: 0
    }]
  };

  const aiPct = totalEmpresas > 0 ? Math.round((pimPerformanceRawData.length / totalEmpresas) * 100) : 0;

  // Cálculo de economia: Estimamos 1.5 horas economizadas por empresa processada pela PIM
  const horasEconomizadas = Math.round(pimPerformanceRawData.length * 1.5);
  // FTE (Full Time Equivalent): Quantos funcionários a IA "substitui" (base 160h/mês)
  const fteEquivalente = (horasEconomizadas / 160).toFixed(1);

  const automationRadarStats = dashboardData?.automationRadarStats || {
    aiPercentage: aiPct,
    hoursSaved: horasEconomizadas,
    fte: fteEquivalente,
    openFinancePercentage: totalEmpresas > 0 ? Math.round((openFinanceRawData.length / totalEmpresas) * 100) : 0,
    rulesPercentage: 0
  };

  const stats = {
    total: totalEmpresas,
    conciliadas: bancoCount,
    pendentes: (planilhaCount + caixaCount),
    semInformacao: semInfoCount,
    percentConciliada: totalEmpresas > 0 ? Math.round((bancoCount / totalEmpresas) * 100) : 0,
    percentPendente: totalEmpresas > 0 ? Math.round(((planilhaCount + caixaCount) / totalEmpresas) * 100) : 0,
    regimeBreakdown: empresas.reduce((acc, emp) => {
      const regime = emp.regimeTributario?.id || 'Outros';
      acc[regime] = (acc[regime] || 0) + 1;
      return acc;
    }, {}),
    segmentoBreakdown: empresas.reduce((acc, emp) => {
      const segmento = emp.segmentoEmpresa?.id || 'Outros';
      acc[segmento] = (acc[segmento] || 0) + 1;
      return acc;
    }, {}),
    openFinanceCount: openFinanceRawData.length,
    pimCount: pimPerformanceRawData.length,
    nivelBCount: bancoCount,
    healthScore: useMemo(() => {
      const ofIds = new Set(openFinanceRawData.map(c => c.id));
      const pimIds = new Set(pimPerformanceRawData.map(c => c.id));
      const scores = empresas.map(emp => {
        let points = 0;
        if (ofIds.has(emp.id)) points++;
        if (emp.nivelContabil === 'B') points++;
        if (pimIds.has(emp.id)) points++;
        return points;
      });

      const totalPossiblePoints = empresas.length * 3;
      const totalPoints = scores.reduce((a, b) => a + b, 0);
      const globalPercent = totalPossiblePoints > 0 ? Math.round((totalPoints / totalPossiblePoints) * 100) : 0;

      return {
        percent: globalPercent,
        levels: {
          alto: scores.filter(s => s === 3).length, // 100% digital
          medio: scores.filter(s => s === 2).length, // 66% digital
          baixo: scores.filter(s => s === 1).length, // 33% digital
          critico: scores.filter(s => s === 0).length // 0% digital
        }
      };
    }, [empresas, openFinanceRawData, pimPerformanceRawData])
  };


  // Eficiência do PIM por categoria
  const pimEfficiencyByCategory = useMemo(() => {
    const pimIds = new Set(pimPerformanceRawData.map(c => c.id));
    const calculateEfficiency = (groupBy) => {
      const groups = {};
      empresas.forEach(emp => {
        const key = (groupBy === 'regime' ? emp.regimeTributario?.id : emp.segmentoEmpresa?.id) || 'Outros';
        if (!groups[key]) groups[key] = { total: 0, pim: 0 };
        groups[key].total++;
        if (pimIds.has(emp.id)) groups[key].pim++;
      });
      return Object.entries(groups).map(([name, data]) => ({
        name,
        total: data.total,
        pim: data.pim,
        percent: Math.round((data.pim / data.total) * 100)
      })).sort((a, b) => b.percent - a.percent);
    };

    return {
      regime: calculateEfficiency('regime'),
      segmento: calculateEfficiency('segmento')
    };
  }, [empresas, pimPerformanceRawData]);

  const handleExport = () => {
    if (!empresas.length) {
      toast.warning("Não há dados disponíveis para exportar.");
      return;
    }

    try {
      const connectedIds = new Set(openFinanceRawData.map(c => c.id));

      const dataToExport = empresas.map(emp => ({
        'ID Cliente': emp.id,
        'CNPJ': emp.cnpj,
        'Nome Fantasia': emp.nomeFantasia || emp.nome || '-',
        'Carteira ID': emp.carteira?.id || '-',
        'Segmento': emp.segmentoEmpresa?.id || '-',
        'Regime Tributário': emp.regimeTributario?.id || '-',
        'Nível Contábil': emp.nivelContabil || 'Sem Informação',
        'Status Open Finance': connectedIds.has(emp.id) ? 'Conectado' : 'Sem Adesão'
      }));

      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Dashboard Geral");
      XLSX.writeFile(wb, `Dashboard_Contabil_Geral_${dayjs().format('DD-MM-YYYY')}.xlsx`);
      toast.success("Excel geral exportado!");
    } catch (error) {
      toast.error("Erro ao exportar Excel.");
    }
  };

  const handleExportAutomation = () => {
    try {
      const pimIds = new Set(pimPerformanceRawData.map(c => c.id));
      const data = empresas.map(emp => ({
        'ID': emp.id,
        'Empresa': emp.nomeFantasia || emp.nome,
        'Carteira ID': emp.carteira?.id || '-',
        'Tipo de Processamento': pimIds.has(emp.id) ? 'PIM (Automático)' : 'Intervenção Humana'
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Automação");
      XLSX.writeFile(wb, `Relatorio_Automacao_${dayjs().format('DD-MM-YYYY')}.xlsx`);
      toast.success("Relatório de automação exportado!");
    } catch (e) { toast.error("Erro ao exportar."); }
  };

  const handleExportLevels = () => {
    try {
      const data = empresas.map(emp => ({
        'ID': emp.id,
        'Empresa': emp.nomeFantasia || emp.nome,
        'Carteira ID': emp.carteira?.id || '-',
        'Nível Contábil': emp.nivelContabil || 'Sem Informação'
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Níveis Contábeis");
      XLSX.writeFile(wb, `Relatorio_Niveis_Contabeis_${dayjs().format('DD-MM-YYYY')}.xlsx`);
      toast.success("Relatório de níveis exportado!");
    } catch (e) { toast.error("Erro ao exportar."); }
  };

  const handleExportOpenFinance = () => {
    try {
      const connectedIds = new Set(openFinanceRawData.map(c => c.id));
      const data = empresas.map(emp => ({
        'ID': emp.id,
        'Empresa': emp.nomeFantasia || emp.nome,
        'Carteira ID': emp.carteira?.id || '-',
        'Status Open Finance': connectedIds.has(emp.id) ? 'Conectado' : 'Sem Adesão'
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Open Finance");
      XLSX.writeFile(wb, `Relatorio_OpenFinance_${dayjs().format('DD-MM-YYYY')}.xlsx`);
      toast.success("Relatório Open Finance exportado!");
    } catch (e) { toast.error("Erro ao exportar."); }
  };

  const handleExportEvolution = () => {
    toast.info("Dados de evolução histórica ainda não integrados para exportação.");
  };

  const handleExportAnomalies = () => {
    if (!anomaliesData.length) {
      toast.warning("Não há anomalias para exportar.");
      return;
    }

    try {
      const data = anomaliesData.map(item => ({
        'ID Cliente': item.cliente?.id || '-',
        'Empresa': item.cliente?.nomeFantasia || item.cliente?.nome || '-',
        'Carteira ID': item.cliente?.carteira?.id || '-',
        'Mês': item.mes,
        'Ano': item.ano,
        'Tipo Anomalia': item.tipoAnomalia || 'Caixa Negativo',
        'Valor': item.valorNegativo || item.valorAnomalia || 0,
        'Status': item.status || '-'
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Anomalias");
      XLSX.writeFile(wb, `Relatorio_Anomalias_${dayjs().format('DD-MM-YYYY')}.xlsx`);
      toast.success("Relatório de anomalias exportado!");
    } catch (e) { toast.error("Erro ao exportar."); }
  };

  const handleExportActions = () => {
    toast.info("Lista de ações críticas vazia no momento.");
  };

  const handleExportObligations = () => {
    if (!obligationsData.length) {
      toast.warning("Não há dados de obrigações para exportar.");
      return;
    }

    const METADATA = {
      1: "30/12", 9: "31/03", 14: "31/05", 15: "31/07", 24: "15/03", 25: "15/03",
      39: "Especial", 40: "Especial", 41: "30/12", 65: "Abertura", 66: "Anual",
      255: "Semestral", 1994: "31/05", 2586: "Abril", 2587: "Outubro", 2757: "Mensal", 2759: "Mensal"
    };

    try {
      const data = obligationsData.map(item => ({
        'Sigla': item.obrigacao.sigla,
        'Obrigação': item.obrigacao.nome,
        'Prazo/Referência': METADATA[item.obrigacao.id] || "N/A",
        'Previsto': item.quantidadePrevisto,
        'Emitido': item.quantidadeEmitido,
        'Progresso': `${item.quantidadePrevisto > 0 ? Math.round((item.quantidadeEmitido / item.quantidadePrevisto) * 100) : 0}%`
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Obrigações");
      XLSX.writeFile(wb, `Relatorio_Obrigacoes_${dayjs().format('DD-MM-YYYY')}.xlsx`);
      toast.success("Relatório de obrigações exportado!");
    } catch (e) { toast.error("Erro ao exportar."); }
  };

  return (
    <Modal
      title={title || "Dashboard de Gestão Contábil"}
      open={visible}
      onCancel={onClose}
      width={1300}
      footer={null}
      zIndex={zIndex}
      getContainer={() => document.body}
      {...draggableProps}
      styles={{
        ...draggableProps.styles,
        body: {
          padding: '0 24px 40px 24px',
          background: '#f8fafc',
          maxHeight: '85vh',
          overflowY: 'auto'
        }
      }}
    >
      <DashboardHeader periodo={periodo} setPeriodo={setPeriodo} onExport={handleExport} />

      <div style={{ position: 'relative' }}>
        {loading && !dashboardData && (
          <div style={{ marginBottom: 24 }}>
            <Skeleton active paragraph={{ rows: 2 }} />
            <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
              <Col span={12}><Skeleton.Node active style={{ width: '100%', height: 400 }} /></Col>
              <Col span={12}><Skeleton.Node active style={{ width: '100%', height: 400 }} /></Col>
            </Row>
          </div>
        )}

        <div style={{ opacity: loading && !dashboardData ? 0 : 1 }}>
          <OperationStats stats={stats} loading={loading} empresas={empresas} />

          <Row gutter={[24, 24]}>
            <Col xs={24} lg={12}>
              <AutomationRadar
                data={automationRadarData}
                stats={automationRadarStats}
                efficiencyData={pimEfficiencyByCategory}
                chartOptions={chartOptions}
                loading={loading}
                onExport={handleExportAutomation}
              />
            </Col>

            <Col xs={24} lg={12}>
              <AnomalyAlerts 
                loading={loading} 
                data={anomaliesData} 
                onExport={handleExportAnomalies}
              />
            </Col>
          </Row>

          <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
            <Col xs={24} lg={10}>
              <AccountingLevelMix
                data={nivelContabilData}
                chartOptions={chartOptions}
                loading={loading}
                onExport={handleExportLevels}
              />
            </Col>

            <Col xs={24} lg={14}>
              <MonthlyEvolution
                data={evolutionData}
                chartOptions={chartOptions}
                loading={loading}
                onExport={handleExportEvolution}
              />
            </Col>
          </Row>

          <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
            <Col xs={24} lg={12}>
              <OpenFinanceStatus
                data={openFinanceData}
                chartOptions={chartOptions}
                loading={loading}
                onExport={handleExportOpenFinance}
              />
            </Col>
            <Col xs={24} lg={12}>
              <YearlyObligations
                loading={loading}
                data={obligationsData}
                onExport={handleExportObligations}
                dataPosicao={periodo ? periodo[1] : dayjs()}
              />
            </Col>
          </Row>

          <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
            <Col xs={24} lg={24}>
              <CriticalActions loading={loading} onExport={handleExportActions} />
            </Col>
          </Row>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
        .ant-modal-content { padding: 0 !important; overflow: hidden !important; border-radius: 24px !important; }
        .ant-modal-header { padding: 16px 24px !important; margin: 0 !important; border-bottom: 1px solid #f1f5f9 !important; }
        .ant-card-head { border: none !important; padding: 24px 24px 10px 24px !important; }
        .ant-picker { border-radius: 12px !important; }
        .ant-progress-inner { background-color: #f1f5f9 !important; }
      `}} />
    </Modal>
  );
}
